import { Link } from "react-router-dom";
import { ArrowRight, Scale, Tags, Zap, Plane, Eye, Layers, UserCheck } from "lucide-react";
import { MaskedLine, Reveal, usePageTitle } from "../components/Motion";
import { Seo } from "../components/Seo";
import Marquee from "../components/Marquee";

const PILLARS = [
    {
        icon: Eye,
        title: "Clarity",
        desc: "Show useful information simply, so decisions feel easier — not overwhelming.",
    },
    {
        icon: Layers,
        title: "One place",
        desc: "Bring product comparison, deals, coupons and travel discovery together in a single app.",
    },
    {
        icon: UserCheck,
        title: "User-first",
        desc: "Help people make more informed decisions, without pushing them toward any platform.",
    },
];

const BRINGS_TOGETHER = [
    { icon: Scale, label: "Product price comparison" },
    { icon: Tags, label: "Coupons, offers & deals" },
    { icon: Zap, label: "Quick-commerce discovery" },
    { icon: Plane, label: "Flights & hotel exploration" },
];

const AboutPage = () => {
    usePageTitle("About — SahiKhoj");
    return (
        <>
            <Seo
                title="About — SahiKhoj"
                description="What SahiKhoj is: a smart shopping and travel comparison app bringing deals, coupons, AI Search, price comparison, flights and hotels together."
                path="/about"
            />
            <section data-testid="about-hero" className="hero-glow bg-ink pb-20 pt-40 text-cream sm:pb-28 lg:pt-48">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        About SahiKhoj
                    </p>
                    <h1 className="mt-7 max-w-4xl font-display text-5xl font-semibold leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.2}>Smarter choices,</MaskedLine>
                        <MaskedLine delay={0.35} className="text-saffron">
                            made simple.
                        </MaskedLine>
                    </h1>
                </div>
            </section>

            <section className="bg-cream py-24 sm:py-32">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
                    <Reveal className="lg:col-span-4">
                        <p className="font-mono text-sm text-saffron">01</p>
                        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
                            The idea
                        </h2>
                    </Reveal>
                    <div className="lg:col-span-8">
                        <Reveal delay={0.1}>
                            <p
                                data-testid="about-mission"
                                className="font-display text-2xl font-medium leading-snug tracking-tight text-ink sm:text-3xl"
                            >
                                SahiKhoj is designed to make online shopping and travel
                                discovery simpler. The app brings product comparison,
                                deals, coupons and travel discovery features together so
                                users can make more informed decisions.
                            </p>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <p className="mt-6 max-w-2xl text-base leading-relaxed text-smoke sm:text-lg">
                                Prices, offers and travel options are spread across many
                                platforms. SahiKhoj exists to reduce that effort — one
                                search inside the app helps you review the options that
                                matter, across supported platforms.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-t border-ink/5 bg-cream pb-24 sm:pb-32">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-sm text-saffron">02</p>
                        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
                            What the app brings together
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {BRINGS_TOGETHER.map(({ icon: Icon, label }, i) => (
                            <Reveal key={label} delay={i * 0.07}>
                                <div
                                    data-testid={`about-pillar-${i + 1}`}
                                    className="flex h-full flex-col gap-4 rounded-2xl border border-ink/5 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                                >
                                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-saffron-soft">
                                        <Icon className="h-5 w-5 text-saffron" />
                                    </span>
                                    <p className="font-display text-lg font-medium tracking-tight text-ink">
                                        {label}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-ink py-24 text-cream sm:py-32">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-sm text-saffron">03</p>
                        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                            How we think
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {PILLARS.map(({ icon: Icon, title, desc }, i) => (
                            <Reveal key={title} delay={i * 0.08}>
                                <div className="h-full rounded-2xl border border-cream/10 bg-cream/5 p-8 transition-colors duration-300 hover:border-saffron/40">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-saffron">
                                        <Icon className="h-5 w-5 text-white" />
                                    </span>
                                    <h3 className="mt-6 font-display text-xl font-semibold tracking-tight">
                                        {title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-mist">
                                        {desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl border border-cream/10 bg-cream/5 p-8 sm:flex-row sm:items-center">
                            <p className="max-w-xl text-sm leading-relaxed text-mist">
                                This website is the official home of SahiKhoj. You can
                                browse live offers, coupons, price comparisons and travel
                                search right here — the SahiKhoj mobile app brings the
                                same tools to your pocket.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="about-contact-link"
                                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-saffron px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-saffron-hover"
                            >
                                Contact us
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
            <section className="border-t border-ink/5 bg-cream py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-sm text-saffron">04</p>
                        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
                            How our content & data work
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
                        {[
                            {
                                title: "Price comparison",
                                desc: "Prices, stores and availability come from the respective platforms' listing data and are shown as received at the time of lookup. Prices can change at any moment — always confirm the final payable amount on the store before paying.",
                            },
                            {
                                title: "Offers & coupons",
                                desc: "Deals and coupon codes are collected from supported platforms and re-checked regularly. A code's validity can change or expire without notice; the store's checkout is always the final word.",
                            },
                            {
                                title: "Flights & hotels",
                                desc: "Fares and rates are presented from travel providers for comparison. Bookings are completed on the provider's own website under its terms — SahiKhoj does not issue tickets or reservations.",
                            },
                            {
                                title: "Blog & editorial",
                                desc: "Guides are researched and reviewed by the SahiKhoj editorial team. Some drafts are AI-assisted — always human fact-checked before publishing, and labelled as AI-assisted on the page.",
                            },
                        ].map(({ title, desc }, i) => (
                            <Reveal key={title} delay={i * 0.06}>
                                <div
                                    data-testid={`about-transparency-${i + 1}`}
                                    className="h-full rounded-2xl border border-ink/5 bg-white p-7 shadow-sm"
                                >
                                    <h3 className="font-display text-lg font-semibold tracking-tight text-ink">{title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-smoke">{desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <p className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                            <Link to="/editorial-policy" data-testid="about-editorial-link" className="font-medium text-india underline decoration-saffron/50 underline-offset-4 transition-colors hover:text-saffron">
                                Read our Editorial Policy
                            </Link>
                            <Link to="/affiliate-disclosure" data-testid="about-affiliate-link" className="font-medium text-india underline decoration-saffron/50 underline-offset-4 transition-colors hover:text-saffron">
                                Read our Affiliate Disclosure
                            </Link>
                        </p>
                    </Reveal>
                </div>
            </section>
            <Marquee />
        </>
    );
};

export default AboutPage;
